"use client";

import { useEffect, useState } from "react";

import type { SoundEffectExploreItem } from "@ui/components/audio/CategoryExploreList";
import type { GeneratedSoundItem, SuggestionPill } from "@ui/components/audio/AudioGenerationWidget";
import type { DownloadableMusicItem } from "./hooks/useMusicDownload";

import { AudioPlayerBar } from "@ui/components/audio/AudioPlayerBar";
import { MusicGenerationWidget } from "@ui/components/music/MusicGenerationWidget";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import clsx from "clsx";

interface MusicStickyBottomWidgetProps {
  activeItem: SoundEffectExploreItem | null;
  generationExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  generationPrompt: string;
  generateItems: (prompt: string, loop: boolean) => Promise<GeneratedSoundItem[]>;
  onDownload: (item: DownloadableMusicItem) => void;
  onGenerateNew: () => void;
  signUpHref: string;
  bumpKey: number;
  hideGenerationWidget?: boolean;
  generationSuggestions?: SuggestionPill[];
  clickableGenerationResultRows?: boolean;
}

export function MusicStickyBottomWidget({
  activeItem,
  generationExpanded,
  onExpandChange,
  generationPrompt,
  generateItems,
  onDownload,
  onGenerateNew,
  signUpHref,
  bumpKey,
  hideGenerationWidget,
  generationSuggestions,
  clickableGenerationResultRows,
}: MusicStickyBottomWidgetProps) {
  const [isPopping, setIsPopping] = useState(false);

  useEffect(() => {
    if (bumpKey === 0) return;
    setIsPopping(true);
    const id = setTimeout(() => setIsPopping(false), 400);
    return () => clearTimeout(id);
  }, [bumpKey]);

  const content = activeItem ? (
    <AudioPlayerBar title={activeItem.title} onDownload={() => onDownload(activeItem)} showProgressBar />
  ) : !hideGenerationWidget ? (
    <MusicGenerationWidget
      isExpanded={generationExpanded}
      onExpandChange={onExpandChange}
      initialPrompt={generationPrompt}
      generateItems={generateItems}
      onDownload={item => onDownload(item)}
      onPlay={(_item, index) => {
        captureEvent(EventName.mw_music_category_generation_played, {
          generationIndex: String(index),
          source: "programmatic",
        });
      }}
      onGenerateNew={onGenerateNew}
      signUpHref={signUpHref}
      focusKey={bumpKey}
      suggestions={generationSuggestions}
      clickableResultRows={clickableGenerationResultRows}
    />
  ) : null;

  if (!content) return null;

  return (
    <div className='tw-fixed tw-bottom-5 tw-left-4 tw-right-4 tw-z-50 tw-flex tw-justify-center lg:tw-left-1/2 lg:tw-right-auto lg:tw-w-auto lg:-tw-translate-x-1/2'>
      <div className={clsx(isPopping && "tw-animate-[pop_0.4s_ease]")}>{content}</div>
    </div>
  );
}
